/**
 * Sprint 14.4 — Frontend permission gating helpers.
 *
 * Resolves the current user's effective permission codes by calling
 * ``GET /api/v1/auth/me/permissions`` once per session (cached by React
 * Query) and exposes a ``hasPermission(code)`` helper.
 *
 * Per the Sprint 14 charter the gating MUST hide, not just disable, UI
 * elements the current user can't act on. Use the ``<Gated>`` wrapper
 * for declarative scoping, or the ``hasPermission`` hook for inline checks.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';

export function useMyPermissions() {
  return useQuery({
    queryKey: ['my-permissions'],
    queryFn: () => v1Get('/auth/me/permissions'),
    // Short TTL so Roles & Permissions matrix saves show up without logout.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function isAdminPayload(data) {
  // Admin shortcut: when the legacy role enum is 'admin' OR any RBAC role
  // code is 'admin', short-circuit to true. The backend's resolve_user_permissions
  // already unions all permissions for admin; this is just a safety net for
  // the brief window between login and the /me/permissions call resolving.
  return data?.legacy_role === 'admin'
    || (data?.roles || []).some((r) => r.code === 'admin');
}

/** Shared allow check used by Gated, RequireAuth, and page-level hooks. */
export function permissionPayloadAllows(data, { need, anyOf, allOf } = {}) {
  if (!data) return false;
  if (isAdminPayload(data)) return true;
  const granted = data.permission_codes || [];
  if (need && granted.includes(need)) return true;
  if (anyOf && anyOf.some((c) => granted.includes(c))) return true;
  if (allOf && allOf.length && allOf.every((c) => granted.includes(c))) return true;
  return false;
}

export function useHasPermission(code) {
  const { data } = useMyPermissions();
  if (!data) return false;
  if (isAdminPayload(data)) return true;
  return (data.permission_codes || []).includes(code);
}

/**
 * Same decision as ``useHasPermission``, plus whether the permission set has
 * actually resolved.
 *
 * ``Gated`` can render nothing while the query is in flight, but a form that
 * flips between editable and read-only needs to distinguish "denied" from "not
 * known yet" so it can fail closed without telling the user they are
 * unauthorized before the answer has arrived.
 */
export function usePermissionGate(code) {
  const { data, isLoading, isError } = useMyPermissions();
  const resolved = !!data && !isLoading;
  const allowed = resolved
    && (isAdminPayload(data) || (data.permission_codes || []).includes(code));
  return { allowed, resolved, isError };
}

/**
 * Declarative permission gate.
 *
 * <Gated need="admin.rbac.manage">
 *   <Button>Open RBAC matrix</Button>
 * </Gated>
 *
 * Renders ``fallback`` (default: null) when the current user lacks the
 * permission. Use this everywhere the user is supposed to NOT SEE the
 * element — not just to disable it.
 */
export function Gated({ need, anyOf, allOf, fallback = null, children }) {
  const { data } = useMyPermissions();
  const ok = permissionPayloadAllows(data, { need, anyOf, allOf });
  return ok ? <>{children}</> : fallback;
}
